import { FC } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { NotificationHooks, Stakeholder, useLocale } from '../../../hooks/index';
import { activityNotificationBuilder, NotificationType } from '../../../types/notification.types';
import { SharePlanType } from '../../../types/pool-plans.types';
import { StakeholderType, TerminationCause } from '../../../types/stakeholder.types';
import { EmptyState, EmptyStateImageVariant, EmptyStateVariant } from '../../empty-state/index';
import Loader from '../../loader/index';
import { ActivityNotification } from '../notification/index';

export type ActivityTabProps = {
  companyId: string;
  currencySymbol: string;
  handleCloseModal: () => void;
  isAllRead: boolean;
  onExerciseRequest: (id: string, type: SharePlanType | undefined, isTerminated?: boolean) => void;
  unreadOnly: boolean;
};

export const ActivityTab: FC<ActivityTabProps> = ({
  companyId,
  unreadOnly,
  isAllRead,
  currencySymbol,
  handleCloseModal,
  onExerciseRequest,
}) => {
  const { notifications, fetchNextPage, hasNextPage, invalidateQuery } =
    NotificationHooks.useNotifications({
      companyId,
      resolved: false,
      type: NotificationType.ACTIVITY,
      unreadOnly,
    });
  const { invite } = Stakeholder.useInvitation();
  const { messagesLocale } = useLocale();
  const { markAsRead } = NotificationHooks.useMarkRead({ companyId, onSuccess: invalidateQuery });

  return notifications.length ? (
    <InfiniteScroll
      className="flex w-full flex-col gap-2"
      dataLength={notifications.length || 0}
      hasMore={hasNextPage}
      loader={<Loader />}
      next={fetchNextPage}
    >
      {notifications.map(
        ({
          createdAt,
          is_read: isRead,
          id,
          action,
          company,
          adminId,
          adminName = '',
          stakeholderName = '',
          stakeholderType = StakeholderType.OTHER_INST,
          type,
          stakeholderId,
          terminationType,
          bulkCount,
          stakeholderTypeGroup,
          dividendPayoutId,
          eventName,
          sharePlanType,
          sharesCount,
          grantId,
          fromName,
          totalInvestment,
          totalShares,
          sharePrice,
          preMoneyValuation,
          percentage,
          payout,
          period,
          date,
        }) => (
          <ActivityNotification
            createdAt={new Date(createdAt)}
            dividendPayoutId={dividendPayoutId}
            handleCloseModal={handleCloseModal}
            isRead={isAllRead || isRead}
            key={id}
            message={activityNotificationBuilder({
              action,
              adminName,
              stakeholderName,
              isAdminYou: adminId === company?.stakeholder?.id,
              stakeholderType,
              terminationType: terminationType || TerminationCause.RETIREMENT,
              currencySymbol,
              bulkCount,
              stakeholderTypeGroup,
              eventName,
              sharePlanType,
              sharesCount,
              grantId,
              fromName,
              totalInvestment,
              totalShares,
              sharePrice,
              preMoneyValuation,
              percentage,
              payout,
              period,
              date,
            })}
            onExercise={() => onExerciseRequest(id, sharePlanType, true)}
            onInviteUser={() =>
              stakeholderId && invite({ stakeholderId }, { onSuccess: invalidateQuery })
            }
            onMarkAsRead={() => markAsRead({ id, type })}
            type={action}
          />
        ),
      )}
    </InfiniteScroll>
  ) : (
    <EmptyState
      colorScheme={EmptyStateVariant.EMPTY}
      description={messagesLocale['YOUR_NOTIFICATION_WILL_BE_HERE']}
      imageVariant={unreadOnly ? EmptyStateImageVariant.CONFETTI : EmptyStateImageVariant.BELL}
      text={unreadOnly ? messagesLocale['ALL_CAUGHT_UP'] : messagesLocale['NO_NOTIFICATIONS']}
    />
  );
};
